<script setup lang="ts">
import { NIcon, NFlex, NAvatar, NDrawer, NDrawerContent, NText, NBadge } from "naive-ui"
import { useAuth } from "@/composables/auth/useAuth"
import {
  CloseCircleOutline,
  LogOutOutline,
  NewspaperOutline,
  PersonCircleOutline,
  SettingsOutline,
} from "@vicons/ionicons5"
import { watch, onMounted } from "vue"
import { useRouter } from "vue-router"
import { useProfilePictureRefresh } from "@/composables/profile/useProfilePictureRefresh"
import { useProfile } from "@/composables/profile/useProfile"
import { baseWhite } from "@/themes/utils"
import LogIcon from "@/assets/brand/logs.svg?component"

const props = defineProps<{ show: boolean }>()
const emit = defineEmits<{ close: [] }>()

const router = useRouter()
const { user, signOut } = useAuth()
const { refreshTrigger, cachedProfilePictureUrl, loadProfilePicture } = useProfilePictureRefresh()
const { profileData, fetchProfile } = useProfile()

interface MenuItem {
  id: string
  label: string
  icon?: any
  route?: string
  action?: () => void
  badge?: string
  disabled?: boolean
}

const menuItems: MenuItem[] = [
  {
    id: "profile",
    label: "Profile",
    icon: PersonCircleOutline,
    route: "/profile",
  },
  {
    id: "news",
    label: "News",
    icon: NewspaperOutline,
    badge: "Coming Soon",
    disabled: true,
  },
  {
    id: "activity",
    label: "Activity Log",
    icon: LogIcon,
    route: "/activity",
  },
  {
    id: "settings",
    label: "Settings",
    icon: SettingsOutline,
    route: "/settings",
  },
]

function handleMenuItemClick(item: MenuItem) {
  if (item.disabled) return

  if (item.route) {
    router.push(item.route)
    emit("close")
  } else if (item.action) {
    item.action()
  }
}

function handleLogout() {
  signOut()
  emit("close")
}

function handleKeyDown(event: KeyboardEvent, item: MenuItem) {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault()
    handleMenuItemClick(item)
  }
}

function handleLogoutKeyDown(event: KeyboardEvent) {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault()
    handleLogout()
  }
}

function onClose() {
  emit("close")
}

// Watch for user changes (login/logout)
watch(
  user,
  () => {
    loadProfilePicture(user.value?.id)
  },
  { immediate: true },
)

// Watch for manual refresh triggers (e.g., after profile picture upload)
watch(refreshTrigger, () => {
  loadProfilePicture(user.value?.id)
})

onMounted(() => {
  loadProfilePicture(user.value?.id)
})

// Profile data auto-initializes via useProfile() composable
// No need to fetch on drawer open - data is already cached
</script>

<template>
  <n-drawer
    v-model:show="props.show"
    placement="left"
    :close-on-esc="true"
    :mask-closable="true"
    @after-leave="emit('close')"
    :width="300">
    <n-drawer-content :native-scrollbar="false">
      <template #header>
        <n-flex align="center" :size="12">
          <n-avatar
            round
            size="large"
            :src="cachedProfilePictureUrl || undefined"
            :fallback-src="`https://api.dicebear.com/7.x/initials/svg?seed=${user?.email}`"
            alt="User profile picture" />
          <n-flex vertical :size="2">
            <n-text strong>
              {{ profileData?.username || user?.user_metadata?.username || "User" }}
            </n-text>
          </n-flex>
        </n-flex>
      </template>

      <!-- Custom Accessible Menu -->
      <nav aria-label="Main menu">
        <ul
          role="menu"
          style="
            list-style: none;
            padding: 0;
            margin: 0;
            display: flex;
            flex-direction: column;
            gap: 8px;
          ">
          <li
            v-for="item in menuItems"
            :key="item.id"
            role="none"
            style="margin: 0">
            <div
              role="menuitem"
              :aria-label="item.label"
              :aria-disabled="item.disabled"
              :tabindex="item.disabled ? -1 : 0"
              @click="handleMenuItemClick(item)"
              @keydown="handleKeyDown($event, item)"
              :style="{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                cursor: item.disabled ? 'not-allowed' : 'pointer',
                borderRadius: '8px',
                transition: 'background-color 0.2s ease',
                opacity: item.disabled ? 0.5 : 1,
              }"
              :class="item.disabled ? '' : 'menu-item'">
              <n-icon
                v-if="item.icon"
                :size="25"
                :component="item.icon"
                :color="baseWhite" />
              <n-flex
                align="center"
                justify="space-between"
                style="flex: 1; gap: 8px">
                <n-text style="font-size: 15px">{{ item.label }}</n-text>
                <n-badge
                  v-if="item.badge"
                  :value="item.badge"
                  type="info"
                  :show-zero="false" />
              </n-flex>
            </div>
          </li>
        </ul>
      </nav>

      <template #footer>
        <n-flex justify="space-between" class="w-100">
          <div
            role="menuitem"
            aria-label="Log out"
            tabindex="0"
            @click="handleLogout"
            @keydown="handleLogoutKeyDown"
            :style="{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer',
              borderRadius: '8px',
              transition: 'background-color 0.2s ease',
            }"
            class="menu-item menu-item-logout">
            <n-icon :size="30" :component="LogOutOutline" :color="baseWhite" />
            <n-text style="font-size: 15px">Log Out</n-text>
          </div>
          <button aria-label="Close" role="button" @click="onClose">
            <n-icon
              :size="30"
              :color="baseWhite"
              :component="CloseCircleOutline"></n-icon>
          </button>
        </n-flex>
      </template>
    </n-drawer-content>
  </n-drawer>
</template>

<style scoped>
.menu-item:hover {
  background-color: var(--n-item-color-hover);
}

.menu-item:focus {
  outline: 2px solid var(--n-border-color);
  outline-offset: -2px;
}

.menu-item:active {
  background-color: var(--n-item-color-pressed);
}

.menu-item-logout {
  color: var(--n-close-icon-color-hover);
}

.menu-item-logout:hover {
  background-color: var(--n-close-color-hover);
}
</style>
